import { Component, ElementRef, EventEmitter, forwardRef, Inject, Input, NgZone, OnDestroy, OnInit, Output, QueryList, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { SOCKET_TOPIC_THING_LIST } from '../../common/endpoints';
import { PropertyInfo } from '../../common/properties';
import { CustomMatPaginatorIntl } from '../../dashboard-area/shared/custom-mat-paginator-intl.service';
import { Location, LocationSchema, Metric, Tag, Thing, ThingDataItem, WorkCell } from '../../model';
import { AbstractExportContextService } from '../../service/abstract-export-context.service';
import { AddButtonContextService } from '../../service/add-button-context.service';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { PropertyService } from '../../service/property.service';
import { RefreshableWidget, RefresherWidgetService } from '../../service/refresher-widget.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { ButtonActionValue, CustomTableComponent, CustomTableService } from '../../shared/custom-table';
import { ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { TagService } from '../../shared/tags/tag.service';
import { ErrorUtility } from '../../utility/error-utility';
import { SnackbarService } from '../shared/snackbar.service';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { ThingListWidgetV2Service } from '../thing-list/thing-list-widget-v2.service';

@Component({
    selector: 'location-schema-thing-group-widget',
    template: require('./location-schema-thing-group-widget.component.html'),
    styles: [require('../../shared/list-widget-v2/list-widget-v2.css'), require('./location-schema-thing-group-widget.component.css')],
    providers: [ThingListWidgetV2Service, TagService, { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }]
})
export class LocationSchemaThingGroupWidgetComponent extends ListWidgetV2Component<Thing> implements OnInit, OnDestroy, RefreshableWidget {

    @Input() inputColumnComponents: QueryList<any>;

    @Input() thingIds: string[];

    @Input() locationSchema: LocationSchema;

    @Input() excludedWorkCellId: string;

    @Input() metrics: Metric[];

    @ViewChild(CustomTableComponent) customTable: CustomTableComponent;

    @Output() deleteDeviceAction = new EventEmitter<string>();

    @Output() moveDeviceToWorkCellAction = new EventEmitter<{ thingId: string, workCellId: string }>();

    things: Thing[];
    location: Location;

    private thingsSubjects: { [thingId: string]: BehaviorSubject<ThingDataItem[]> } = {};
    private metricNames = new Set<string>();
    private unregisterFn: Function;
    private tags: Tag[];

    constructor(
        @Inject(forwardRef(() => ThingListWidgetV2Service)) private thingListWidgetService: ThingListWidgetV2Service,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => PropertyService)) private propertyService: PropertyService,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => RefresherWidgetService)) private refresherWidgetService: RefresherWidgetService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => AbstractExportContextService)) exportService: AbstractExportContextService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => SnackbarService)) snackbarService: SnackbarService,
        @Inject(forwardRef(() => AddButtonContextService)) addButtonService: AddButtonContextService,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService
    ) {
        super(appService, authenticationService, exportService, addButtonService, dialog, snackbarService, null, elementRef, coordinator);
    }

    private defaultProperties: { [name: string]: PropertyInfo } = {
        name: { label: 'thingNameProperty', path: 'name', defaultFilter: null, defaultSorting: null },
        serialNumber: { label: 'serialNumberProperty', path: 'serialNumber', defaultFilter: null, defaultSorting: null },
        gpsPosition: { label: 'gpsPositionProperty', path: 'gpsPosition', defaultFilter: null, defaultSorting: null },
        serviceLevel: { label: 'serviceLevelProperty', path: 'serviceLevel', defaultFilter: 'defaultServiceLevel', defaultSorting: 'service-level-sorting' },
        "customer.name": { label: 'customerNameProperty', path: 'customer.name', defaultFilter: null, defaultSorting: null },
        "location.name": { label: 'locationNameProperty', path: 'location.name', defaultFilter: null, defaultSorting: null }
    };

    ngOnInit(): void {
        this.location = this.contextService.getCurrentLocation();
        this.unregisterFn = this.refresherWidgetService.register(this);
        this.pageSize = 100;
        this.fillerRowCount = 5;
    }

    ngAfterContentInit(): void {
        this.tags = this.contextService.getTagObjects();
        this.thingListWidgetService.setMetrics(this.metrics);
        this.initVisibleColumns();
        if (!this.query) {
            this.getThingList();
        }
    }

    protected initVisibleColumns(): void {
        let availableWorkCells: WorkCell[] = [];
        this.locationSchema.productionLines.forEach(pl => {
            pl.workCells?.forEach(wc => {
                if (wc.id != this.excludedWorkCellId) {
                    availableWorkCells.push(wc);
                }
            })
        });
        this.columnComponentsArray = this.thingListWidgetService.getColumnComponentsArray(this.inputColumnComponents.toArray(), this.defaultProperties);
        this.displayedColumns = this.thingListWidgetService.getVisibleColumns(this.columnComponentsArray, this.defaultProperties, 'Thing', this.id);
        this.displayedColumns.push(
            CustomTableService.newLocationSchemaThingGroupActionColumn('menu', '', availableWorkCells)
                .withStyle({ '_any': { 'width': '10px' } })
                .withStickyEndColumn()
                .withColumnClass('no-print')
        )
        this.displayedColumns.forEach(dc => dc.sortField = null);
        this.descriptions = this.thingListWidgetService.getColumnDescriptions(this.inputColumnComponents.toArray());
        this.metricNames = this.thingListWidgetService.getMetricNames(this.inputColumnComponents);
        this.sort = this.thingListWidgetService.setDefaultSort(this.displayedColumns);
    }

    private getThingList(): void {
        if (!this.advancedSearchBody) {
            this.advancedSearchBody = {};
        }
        this.advancedSearchBody.selectedThingIds = this.thingIds;
        this.thingListWidgetService.getPagedList(this.pageIndex, this.pageSize, this.sort, this.metricNames, [], this.advancedSearchBody, this.location, true, false, null, false).then(pagedList => {
            this.things = pagedList.content;
            this.error = null;
            this.subscribeToThings();
            this.thingListWidgetService.addTags(pagedList.content, this.tags || []);
            this.updateElementList(pagedList);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    private subscribeToThings(): void {
        for (let thing of this.things) {
            let thingSubject = this.propertyService.subscribeToThingProperties(thing.id);
            this.thingsSubjects[thing.id] = thingSubject;
            thingSubject.subscribe((thingEvent: ThingDataItem[]) => this.updateProperties(thing.id, thingEvent));
        }
    }

    private updateProperties(thingId: string, thingEvent: ThingDataItem[]): void {
        if (thingEvent) {
            let thing = this.things.find(t => t.id == thingId);
            for (let thingDataItem of thingEvent) {
                _.set(thing, PropertyService.getFieldName(thingDataItem), thingDataItem.value);
            }
            this.zone.run(() => this.dataSource.data = this.things);
        }
    }

    goToDetail(thing: Thing): void {
        /* do nothing */
    }

    ngOnDestroy(): void {
        if (this.unregisterFn) {
            this.unregisterFn();
        }
        this.unsubscribeAll();
        super.ngOnDestroy();
    }

    private unsubscribeAll(): void {
        for (let thingId in this.thingsSubjects) {
            this.propertyService.usubscribeFromThingProperties(thingId);
        }
        this.thingsSubjects = {};
    }

    getRefreshTopic(): string {
        let userId = this.authenticationService.getUser().id;
        return SOCKET_TOPIC_THING_LIST.replace('{userId}', userId);
    }

    refresh(): void {
        setTimeout(() => {
            this.zone.run(() => {
                this.loaded = false;
                this.unsubscribeAll();
                this.getThingList();
            });
        }, 1000);
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.unsubscribeAll();
        this.getThingList();
    }

    protected subscribeToExportServices(): void {
        /* do nothing */
    }

    handleButtonAction(actionValue: ButtonActionValue): void {
        if (actionValue.action == 'DELETE_DEVICE') {
            this.deleteDeviceAction.emit(actionValue.value.thingId);
        } else if (actionValue.action == 'MOVE_DEVICE_TO_WORK_CELL') {
            this.moveDeviceToWorkCellAction.emit({ thingId: actionValue.value.thingId, workCellId: actionValue.value.workCellId });
        }
    }

}

